/**
 * EmailRenderer — Render HTML email content safely with DOMPurify.
 * Simple approach: sanitize HTML and render directly via dangerouslySetInnerHTML.
 */
import { useMemo } from 'react';
import DOMPurify from 'dompurify';

interface EmailRendererProps {
    bodyHtml?: string | null;
    bodyText: string;
}

function extractContent(raw: string): string {
    // If it's a full HTML document, extract body + styles
    if (raw.includes('<body')) {
        let bodyContent = '';
        let styleContent = '';

        // Extract body
        const bodyMatch = raw.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        if (bodyMatch) {
            bodyContent = bodyMatch[1];
        } else {
            // Truncated — take everything after <body...>
            const start = raw.match(/<body[^>]*>/i);
            if (start) {
                bodyContent = raw.substring(raw.indexOf(start[0]) + start[0].length);
            } else {
                bodyContent = raw;
            }
        }

        // Extract styles
        const styles = raw.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
        if (styles) styleContent = styles.join('\n');

        return styleContent + bodyContent;
    }
    return raw;
}

export function EmailRenderer({ bodyHtml, bodyText }: EmailRendererProps) {
    const renderedHtml = useMemo(() => {
        let raw = (bodyHtml && bodyHtml.trim().length > 0) ? bodyHtml : (bodyText || '');
        if (!raw.trim()) return '<p style="color:#666">No content</p>';

        const isHtml = raw.includes('<') && raw.includes('>') && (raw.includes('</') || raw.includes('/>'));

        if (!isHtml) {
            // Plain text → format with links
            return raw
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/(https?:\/\/[^\s<"]+)/g, '<a href="$1" target="_blank" rel="noopener" style="color:#60a5fa">$1</a>')
                .replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '<a href="mailto:$1" style="color:#60a5fa">$1</a>')
                .replace(/\n/g, '<br>');
        }

        // Extract content from full HTML documents
        const content = extractContent(raw);

        // Sanitize
        return DOMPurify.sanitize(content, {
            ADD_TAGS: ['style'],
            ADD_ATTR: ['style', 'class', 'bgcolor', 'border', 'cellpadding', 'cellspacing', 'width', 'height', 'align', 'valign', 'target', 'rel'],
            ALLOW_DATA_ATTR: false,
            KEEP_CONTENT: true,
            ALLOWED_TAGS: [
                'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                'p', 'div', 'span', 'a', 'img',
                'table', 'tr', 'td', 'th', 'tbody', 'thead', 'tfoot',
                'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
                'strong', 'em', 'b', 'i', 'u', 'strike', 's', 'del',
                'br', 'hr', 'style', 'font', 'center', 'sup', 'sub',
            ],
        });
    }, [bodyHtml, bodyText]);

    return (
        <div
            className="email-rendered-content"
            style={{
                padding: 16,
                fontSize: 14,
                lineHeight: 1.6,
                color: '#e0e0e0',
                overflowWrap: 'break-word',
                wordWrap: 'break-word',
            }}
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
        />
    );
}
